import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidePanel, SidePanelFooter } from './SidePanel';
import { Button } from '../ui/Button';
import { StatusDot } from '../ui/Badge';
import { useAppStore } from '@/stores/appStore';
import { XMarkIcon, ShareIcon, CheckIcon } from '@heroicons/react/24/outline';
import { ICON_MAP } from './IconPicker';
import { AuditLogFeed, type AuditLogEntry } from '../ui/AuditLogFeed';
import { CommentsTab } from '../comments';
import { DependenciesTab } from '../dependencies/DependenciesTab';
import { ResourcesTab } from '../resources/ResourcesTab';
import { ScoringTab } from '../scoring/ScoringTab';
import { VoteButton } from '../scoring/VoteButton';
import { initiativeApi, commentApi, scoringApi } from '@/services/api';
import { JiraPushModal } from './JiraPushModal';
import { useCanEdit } from '@/contexts/AuthContext';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { InitiativeIntelligencePanel } from '@/pages/InitiativeIntelligencePanel';
import { SparklesIcon } from '@heroicons/react/24/outline';
import type { Initiative } from '@/types';
import { formatActualDate, formatFiscalQuarterHint } from '@/utils/dateRange';

// Tab definitions
type TabId = 'main' | 'dependencies' | 'comments' | 'audit' | 'resources' | 'intelligence' | 'scoring';

interface InitiativeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  initiative: Initiative | null;
  onEdit: () => void;
}

export function InitiativeDetailModal({
  isOpen,
  onClose,
  initiative,
  onEdit,
}: InitiativeDetailModalProps) {
  const { products, quarters, initialModalTab, updateInitiativeStarred } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabId>('main');
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [dependencyCount, setDependencyCount] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [jiraPushOpen, setJiraPushOpen] = useState(false);
  const [localJiraKey, setLocalJiraKey] = useState<string | null>(null);
  const [localJiraUrl, setLocalJiraUrl] = useState<string | null>(null);
  const [localIsStarred, setLocalIsStarred] = useState(initiative?.isStarred ?? false);
  const [voteCount, setVoteCount] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const navigate = useNavigate();

  // Check if current user can edit this initiative
  const canEdit = useCanEdit(initiative?.userId);
  const labs = useLabsFeatures();
  const fc = useFieldConfig();

  // Tab definitions — conditionally include labs-gated tabs
  const TABS: { id: TabId; label: string | React.ReactNode }[] = [
    { id: 'main', label: 'Main' },
    ...(labs.isEnabled('dependencies') ? [{
      id: 'dependencies' as TabId,
      label: (
        <span className="inline-flex items-center gap-1.5">
          Dependencies
          {dependencyCount > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {dependencyCount}
            </span>
          )}
        </span>
      ),
    }] : []),
    ...(labs.isEnabled('comments') ? [{
      id: 'comments' as TabId,
      label: (
        <span className="inline-flex items-center gap-1.5">
          Comments
          {commentCount > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {commentCount}
            </span>
          )}
        </span>
      ),
    }] : []),
    ...(labs.isEnabled('auditLog') ? [{ id: 'audit' as TabId, label: 'Audit Log' }] : []),
    ...(labs.isEnabled('resources') ? [{ id: 'resources' as TabId, label: 'Resources' }] : []),
    ...(labs.isEnabled('scoring') ? [{ id: 'scoring' as TabId, label: 'Scoring' }] : []),
    ...(labs.isEnabled('intelligenceItemInsights') ? [{
      id: 'intelligence' as TabId,
      label: (
        <span className="inline-flex items-center gap-1.5">
          <SparklesIcon className="w-3.5 h-3.5 text-amber-500" />
          Intelligence
        </span>
      ),
    }] : []),
  ];

  // Set initial tab when modal opens; fall back to 'main' if the requested tab is now hidden
  useEffect(() => {
    if (isOpen) {
      const requested = initialModalTab || 'main';
      const available = TABS.map((t) => t.id);
      setActiveTab(available.includes(requested as TabId) ? (requested as TabId) : 'main');
    }
  }, [isOpen, initialModalTab]);

  // Reset dependency count when modal opens with a new initiative
  useEffect(() => {
    if (isOpen && initiative) {
      const activeDeps = initiative.dependencies?.filter(d => d.isActive !== false) || [];
      setDependencyCount(activeDeps.length);
    } else {
      setDependencyCount(0);
    }
  }, [isOpen, initiative?.id]);

  // Fetch comment count when modal opens
  useEffect(() => {
    if (isOpen && initiative?.id) {
      commentApi.getCount(initiative.id)
        .then(({ count }) => setCommentCount(count))
        .catch(() => setCommentCount(0));
    } else {
      setCommentCount(0);
    }
  }, [isOpen, initiative?.id]);

  // Fetch audit log when switching to audit tab
  useEffect(() => {
    if (activeTab === 'audit' && initiative?.id) {
      setAuditLogLoading(true);
      initiativeApi
        .getAuditLog(initiative.id)
        .then((data) => setAuditLog(data))
        .catch((err) => console.error('Failed to load audit log:', err))
        .finally(() => setAuditLogLoading(false));
    }
  }, [activeTab, initiative?.id]);

  // Sync local jira state when initiative changes
  useEffect(() => {
    if (isOpen && initiative) {
      setLocalJiraKey(initiative.jiraIssueKey ?? null);
      setLocalJiraUrl(initiative.jiraIssueUrl ?? null);
    }
  }, [isOpen, initiative?.id]);

  // Sync local starred state when initiative changes
  useEffect(() => {
    if (isOpen && initiative) {
      setLocalIsStarred(initiative.isStarred ?? false);
    }
  }, [isOpen, initiative?.id]);

  // Load vote state when modal opens
  useEffect(() => {
    if (isOpen && initiative?.id && labs.isEnabled('scoring')) {
      scoringApi.getVote(initiative.id)
        .then(({ count, hasVoted }) => {
          setVoteCount(count);
          setHasVoted(hasVoted);
        })
        .catch(() => {
          setVoteCount(0);
          setHasVoted(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initiative?.id]);

  if (!initiative) return null;

  // Find product and sub-product names
  let productName = '';
  let subProductName = '';
  for (const product of products) {
    const subProduct = product.subProducts?.find(
      (sp) => sp.id === initiative.subProductId
    );
    if (subProduct) {
      productName = product.name;
      subProductName = subProduct.name;
      break;
    }
  }

  // Get quarter labels
  const getQuarterLabel = (key: string) => {
    const quarter = quarters.find((q) => q.key === key);
    return quarter?.fullLabel || key;
  };

  const classification = initiative.classification;
  const status = initiative.status;
  const owner = initiative.user;

  const handleToggleStar = async () => {
    const newVal = !localIsStarred;
    setLocalIsStarred(newVal);
    updateInitiativeStarred(initiative!.id, newVal);
    try {
      await initiativeApi.update(initiative!.id, { isStarred: newVal });
    } catch {
      setLocalIsStarred(!newVal);
      updateInitiativeStarred(initiative!.id, !newVal);
    }
  };

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} hideHeader>
      {/* Compact header: classification bar + title + close button */}
      <div className="flex items-start gap-3 px-6 py-3 border-b border-border-subtle flex-shrink-0">
        <div
          className="w-1 self-stretch rounded-full flex-shrink-0"
          style={{ backgroundColor: classification?.color || '#3B82F6' }}
        />
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-text-primary leading-snug">
            {initiative.title}
          </h2>
          {classification && (
            <span
              className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded"
              style={{
                backgroundColor: `${classification.color}20`,
                color: classification.color,
              }}
            >
              {classification.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Vote — open to all authenticated users */}
          {labs.isEnabled('scoring') && (
            <VoteButton
              initiativeId={initiative.id}
              count={voteCount}
              hasVoted={hasVoted}
              onChange={(c, v) => { setVoteCount(c); setHasVoted(v); }}
            />
          )}
          <button
            type="button"
            onClick={handleToggleStar}
            className={`rounded-md p-1 transition-colors ${
              localIsStarred
                ? 'text-amber-400 hover:text-amber-500'
                : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
            }`}
            title={localIsStarred ? 'Remove star' : 'Star this initiative'}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill={localIsStarred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => navigate(`/initiatives/${initiative!.id}`)}
            className="rounded-md p-1 text-text-secondary hover:bg-surface-hover hover:text-blue-600 transition-colors"
            title="Open full page"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => {
              const url = `${window.location.origin}/initiative/${initiative.id}`;
              navigator.clipboard.writeText(url).then(() => {
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
              });
            }}
            className="rounded-md p-1 text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
            title="Copy link to initiative"
          >
            {linkCopied ? (
              <CheckIcon className="h-5 w-5 text-green-600" />
            ) : (
              <ShareIcon className="h-5 w-5" />
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Tab Navigation - pinned */}
      <div className="border-b border-border-subtle px-6 flex-shrink-0 overflow-x-auto scrollbar-hide">
        <nav className="flex gap-6 w-max" aria-label="Tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-tour-id={tab.id === 'intelligence' ? 'initiative-intelligence-tab' : undefined}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-default'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Scrollable Tab Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Main Tab */}
        {activeTab === 'main' && (
          <div className="space-y-6">
            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Product / Sub-product */}
              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase mb-1">
                  {fc.label('product')}
                </label>
                <p className="text-sm text-text-primary">{productName}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase mb-1">
                  {fc.label('subProduct')}
                </label>
                <p className="text-sm text-text-primary">{subProductName}</p>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase mb-1">
                  Status
                </label>
                {status && (
                  <div className="flex items-center gap-2">
                    <StatusDot color={status.color} />
                    <span className="text-sm text-text-primary">{status.name}</span>
                  </div>
                )}
              </div>

              {/* Owner */}
              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase mb-1">
                  Owner
                </label>
                <p className="text-sm text-text-primary">{owner?.name || '-'}</p>
              </div>

              {/* Timeline */}
              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase mb-1">
                  Start
                </label>
                {initiative.datePrecision === 'day' ? (
                  <>
                    <p className="text-sm text-text-primary">{formatActualDate(initiative.startDate)}</p>
                    <p className="text-xs text-text-tertiary">{formatFiscalQuarterHint(initiative.startDate, quarters)}</p>
                  </>
                ) : (
                  <p className="text-sm text-text-primary">
                    {getQuarterLabel(initiative.startQuarter ?? '')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase mb-1">
                  End
                </label>
                {initiative.datePrecision === 'day' ? (
                  initiative.endDate ? (
                    <>
                      <p className="text-sm text-text-primary">{formatActualDate(initiative.endDate)}</p>
                      <p className="text-xs text-text-tertiary">{formatFiscalQuarterHint(initiative.endDate, quarters)}</p>
                    </>
                  ) : (
                    <p className="text-sm text-text-primary">-</p>
                  )
                ) : (
                  <p className="text-sm text-text-primary">
                    {initiative.endQuarter
                      ? getQuarterLabel(initiative.endQuarter)
                      : '-'}
                  </p>
                )}
              </div>
            </div>

            {/* Tags */}
            {initiative.tags && initiative.tags.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-text-secondary uppercase mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2">
                  {initiative.tags.map((initTag) => {
                    const IconComponent = initTag.tag?.icon
                      ? ICON_MAP[initTag.tag.icon]
                      : null;
                    return (
                      <div
                        key={initTag.tagId}
                        className="flex items-center gap-1.5 px-2 py-1 bg-surface-subtle rounded-md text-sm text-text-secondary"
                      >
                        {IconComponent && <IconComponent className="w-4 h-4" />}
                        <span>{initTag.tag?.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dependencies Tab */}
        {activeTab === 'dependencies' && (
          <DependenciesTab
            initiativeId={initiative.id}
            onDependencyCountChange={setDependencyCount}
          />
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <CommentsTab
            initiativeId={initiative.id}
            onCommentCountChange={setCommentCount}
          />
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <AuditLogFeed entries={auditLog} loading={auditLogLoading} compact />
        )}

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <ResourcesTab initiativeId={initiative.id} />
        )}

        {/* Intelligence Tab */}
        {activeTab === 'intelligence' && (
          <InitiativeIntelligencePanel initiativeId={initiative.id} />
        )}

        {/* Scoring Tab */}
        {activeTab === 'scoring' && (
          <ScoringTab initiativeId={initiative.id} canEdit={canEdit} />
        )}
      </div>

      {/* Footer - pinned at bottom */}
      <SidePanelFooter>
        <div className="flex items-center justify-between w-full">
          {/* Left: Jira chip if already pushed */}
          <div>
            {(localJiraKey || initiative.jiraIssueKey) && (
              <a
                href={localJiraUrl || initiative.jiraIssueUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 border border-blue-200 rounded-full px-2.5 py-1 hover:bg-blue-50"
              >
                <svg viewBox="0 0 32 32" className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true">
                  <defs>
                    <linearGradient id="jira-chip-grad" x1="100%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#2684FF" />
                      <stop offset="100%" stopColor="#0052CC" />
                    </linearGradient>
                  </defs>
                  <path d="M15.997 1.156L8.344 8.81 4.01 13.144a1.376 1.376 0 000 1.945l7.653 7.654 4.334 4.334a1.376 1.376 0 001.945 0l4.334-4.334 7.653-7.654a1.376 1.376 0 000-1.945l-4.334-4.334-7.598-7.654zm0 9.932l3.877 3.933-3.877 3.877-3.877-3.877 3.877-3.933z" fill="url(#jira-chip-grad)" />
                </svg>
                View {localJiraKey || initiative.jiraIssueKey} in Jira ↗
              </a>
            )}
          </div>

          {/* Right: action buttons */}
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canEdit && labs.isEnabled('jira') && (
              <Button type="button" variant="secondary" onClick={() => setJiraPushOpen(true)}>
                Push to Jira
              </Button>
            )}
            {canEdit && (
              <Button type="button" variant="primary" onClick={onEdit}>
                Edit
              </Button>
            )}
          </div>
        </div>
      </SidePanelFooter>

      {jiraPushOpen && (
        <JiraPushModal
          isOpen={jiraPushOpen}
          onClose={() => setJiraPushOpen(false)}
          initiative={initiative}
          onSuccess={(key, url) => {
            setLocalJiraKey(key);
            setLocalJiraUrl(url);
            setJiraPushOpen(false);
          }}
        />
      )}
    </SidePanel>
  );
}
